/**
 * 商品詳細画面でバリアント選択時に価格を動的に更新するスクリプト
 * Shopify Dawn テーマ対応
 */

class ProductVariantPriceUpdater {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.productContainer = document.querySelector(`#MainProduct-${sectionId}`);
    this.priceContainer = document.querySelector(`#price-${sectionId}`);
    this.variantSelects = document.querySelector(`variant-selects`);
    
    if (!this.productContainer || !this.priceContainer || !this.variantSelects) {
      console.warn('ProductVariantPriceUpdater: 必要な要素が見つかりません');
      return;
    }

    this.variants = this.getVariantData();
    this.currencyFormat = this.getCurrencyFormat();
    this.currentVariantId = null;
    this.updateTimeout = null;
    this.isUpdating = false;
    
    // PUB_SUB_EVENTSとsubscribe/publish関数が利用可能になるまで待つ
    this.waitForDependencies().then(() => {
      this.init();
    });
  }

  async waitForDependencies() {
    let attempts = 0;
    const maxAttempts = 50; // 5秒間待機
    
    while (attempts < maxAttempts) {
      if (typeof PUB_SUB_EVENTS !== 'undefined' && 
          typeof subscribe === 'function' && 
          typeof publish === 'function') {
        console.log('依存関数が利用可能になりました');
        return true;
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    console.warn('依存関数が利用可能になりませんでした。フォールバックモードで動作します。');
    return false;
  }

  init() {
    // PUB_SUB_EVENTSが利用可能な場合のみイベント監視を開始
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      // バリアント変更のイベントを監視
      this.onVariantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
      
      // オプション選択変更のイベントも監視
      this.onOptionChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionChange.bind(this)
      );
      
      console.log('ProductVariantPriceUpdater イベント監視開始:', this.sectionId);
    } else {
      // フォールバックとして直接イベントリスナーを設定
      this.setupFallbackEventListeners();
      console.log('ProductVariantPriceUpdater フォールバックモードで初期化:', this.sectionId);
    }
    
    console.log('ProductVariantPriceUpdater 初期化完了:', {
      sectionId: this.sectionId,
      variants: this.variants.length
    });
  }

  setupFallbackEventListeners() {
    // フォールバック: 直接変更イベントを監視
    if (this.variantSelects) {
      this.variantSelects.addEventListener('change', (event) => {
        this.debouncedUpdate();
      });
    }

    // 全てのselect要素とradio要素も監視
    const allInputs = this.productContainer.querySelectorAll('select, input[type="radio"]');
    allInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.debouncedUpdate();
      });
    });
  }

  getVariantData() {
    // 商品のバリアントデータを取得
    const variantScript = document.querySelector(`script[data-variants="${this.sectionId}"]`);
    if (variantScript) {
      try {
        return JSON.parse(variantScript.textContent);
      } catch (error) {
        console.error('バリアントデータの解析に失敗:', error);
      }
    }
    
    // フォールバック: 既存のproduct-info.jsからデータを取得
    if (window.product && window.product.variants) {
      return window.product.variants;
    }
    
    return [];
  }

  getCurrencyFormat() {
    // 通貨フォーマット設定を取得
    const moneyFormat = window.theme?.moneyFormat || '¥{{amount_no_decimals_with_comma_separator}}';
    const currencyCode = window.shop?.currency || 'JPY';
    
    return {
      format: moneyFormat,
      currency: currencyCode,
      showCurrencyCode: window.theme?.showCurrencyCode || false
    };
  }

  getCurrentVariant() {
    // 現在選択されているバリアントを取得
    if (!this.variantSelects) return this.variants[0] || null;

    const selectedOptionValues = Array.from(
      this.variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(option => option.dataset?.optionValueId || option.value);

    if (selectedOptionValues.length === 0) {
      return this.variants[0] || null;
    }

    // オプション値IDで検索
    let currentVariant = this.variants.find(variant => {
      if (!variant.options || !Array.isArray(variant.options)) return false;
      return variant.options.every((option, index) => {
        const selectedId = selectedOptionValues[index];
        return String(option) === String(selectedId) || String(variant.option1) === String(selectedId);
      });
    });

    // フォールバック: オプション値で直接検索
    if (!currentVariant) {
      const selectedOptionNames = Array.from(
        this.variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
      ).map(option => option.value);

      currentVariant = this.variants.find(variant => 
        variant.options && variant.options.every((option, index) => option === selectedOptionNames[index])
      );
    }

    return currentVariant || this.variants[0] || null;
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || cents === null || cents === undefined) return '';
    
    // Shopifyの標準的な価格フォーマット関数を使用（存在する場合）
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, this.currencyFormat.format);
    }
    
    const amount = cents / 100;
    
    try {
      const formatOptions = {
        style: 'currency',
        currency: this.currencyFormat.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2
      };
      
      return new Intl.NumberFormat('ja-JP', formatOptions).format(amount);
    } catch (error) {
      // フォールバック
      return `¥${amount.toLocaleString('ja-JP')}`;
    }
  }

  updatePriceDisplay() {
    // 更新中の場合はスキップ
    if (this.isUpdating) return;
    
    const currentVariant = this.getCurrentVariant();
    
    if (!currentVariant || !this.priceContainer) {
      console.warn('価格更新: バリアントまたは価格コンテナが見つかりません');
      return;
    }

    // 同じバリアントの場合は更新をスキップ
    if (this.currentVariantId === currentVariant.id) {
      return;
    }

    this.isUpdating = true;
    this.currentVariantId = currentVariant.id;
    console.log('価格表示を更新中:', currentVariant);

    // 現在の価格
    const currentPrice = currentVariant.price;
    const compareAtPrice = currentVariant.compare_at_price;
    const isOnSale = compareAtPrice && compareAtPrice > currentPrice;

    // 在庫状況
    const isAvailable = currentVariant.available;

    // 価格コンテナのクラス更新
    this.updatePriceContainerClasses(isOnSale, isAvailable);

    // 価格表示の更新
    this.updatePriceElements(currentPrice, compareAtPrice, isOnSale);

    // 単価表示の更新（ある場合）
    if (currentVariant.unit_price) {
      this.updateUnitPrice(currentVariant);
    }

    // 在庫表示の更新
    this.updateInventoryDisplay(currentVariant);

    this.isUpdating = false;

    // カスタムイベントを発火
    this.productContainer.dispatchEvent(new CustomEvent('variant-price-updated', {
      detail: {
        variant: currentVariant,
        price: currentPrice,
        compareAtPrice: compareAtPrice,
        formattedPrice: this.formatMoney(currentPrice),
        formattedCompareAtPrice: compareAtPrice ? this.formatMoney(compareAtPrice) : null,
        isOnSale: isOnSale,
        isAvailable: isAvailable
      },
      bubbles: true
    }));
  }

  updatePriceElements(currentPrice, compareAtPrice, isOnSale) {
    // 通常価格の更新
    const regularPriceElement = this.priceContainer.querySelector('.price__regular .price-item--regular');
    const salePriceElement = this.priceContainer.querySelector('.price__sale .price-item--sale');
    const compareAtElement = this.priceContainer.querySelector('.price__sale .price-item--regular');

    if (isOnSale && salePriceElement && compareAtElement) {
      // セール価格の表示
      salePriceElement.textContent = this.formatMoney(currentPrice);
      compareAtElement.textContent = this.formatMoney(compareAtPrice);
    } else if (regularPriceElement) {
      // 通常価格の表示
      regularPriceElement.textContent = this.formatMoney(currentPrice);
    }

    // その他の価格要素も更新
    const allPriceElements = this.priceContainer.querySelectorAll('.price-item:not(.price-item--regular):not(.price-item--sale)');
    allPriceElements.forEach(element => {
      if (element.textContent.includes('¥') || element.textContent.includes('￥')) {
        element.textContent = this.formatMoney(currentPrice);
      }
    });
  }

  updatePriceContainerClasses(isOnSale, isAvailable) {
    if (!this.priceContainer) return;

    const priceDiv = this.priceContainer.querySelector('.price');
    if (!priceDiv) return;

    // 既存のクラスをリセット
    priceDiv.classList.remove('price--on-sale', 'price--sold-out');

    // 新しいクラスを追加
    if (isOnSale) {
      priceDiv.classList.add('price--on-sale');
    }
    if (!isAvailable) {
      priceDiv.classList.add('price--sold-out');
    }

    // 価格セクションの表示制御
    const regularSection = priceDiv.querySelector('.price__regular');
    const saleSection = priceDiv.querySelector('.price__sale');

    if (regularSection && saleSection) {
      if (isOnSale) {
        regularSection.style.display = 'none';
        saleSection.style.display = 'initial';
      } else {
        regularSection.style.display = 'initial';
        saleSection.style.display = 'none';
      }
    }
  }

  updateUnitPrice(variant) {
    const unitPriceElement = this.priceContainer.querySelector('.unit-price');
    if (!unitPriceElement || !variant.unit_price_measurement || !variant.unit_price) return;

    const unitPriceSpan = unitPriceElement.querySelector('.price-item');
    if (unitPriceSpan) {
      // 単価の計算とフォーマット
      const unitPrice = variant.unit_price;
      const measurement = variant.unit_price_measurement;
      
      let unitDisplay = this.formatMoney(unitPrice);
      if (measurement && measurement.reference_unit) {
        unitDisplay += ` / ${measurement.reference_value || 1}${measurement.reference_unit}`;
      }
      
      unitPriceSpan.textContent = unitDisplay;
    }
  }

  updateInventoryDisplay(variant) {
    const inventoryElement = document.querySelector(`#Inventory-${this.sectionId}`);
    if (!inventoryElement) return;

    const isAvailable = variant.available;
    const inventoryQuantity = variant.inventory_quantity;
    const inventoryManagement = variant.inventory_management;

    if (inventoryManagement === 'shopify') {
      if (!isAvailable) {
        // 在庫切れ
        inventoryElement.style.display = 'block';
        inventoryElement.innerHTML = '<span class="svg-wrapper" style="color: rgb(200, 200, 200);">在庫切れ</span>';
      } else if (inventoryQuantity <= 10 && inventoryQuantity > 0) {
        // 低在庫
        inventoryElement.style.display = 'block';
        inventoryElement.innerHTML = `<span class="svg-wrapper" style="color: rgb(238, 148, 65);">在庫残りわずか (${inventoryQuantity}個)</span>`;
      } else {
        // 在庫有り
        inventoryElement.style.display = 'block';
        inventoryElement.innerHTML = '<span class="svg-wrapper" style="color: rgb(62, 214, 96);">在庫有り</span>';
      }
    } else {
      inventoryElement.style.display = 'none';
    }
  }

  debouncedUpdate() {
    // 既存のタイムアウトをクリア
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    // 新しいタイムアウトを設定
    this.updateTimeout = setTimeout(() => {
      this.updatePriceDisplay();
    }, 200);
  }

  handleVariantChange(event) {
    // バリアント変更時のイベントハンドラ
    if (event && event.data && event.data.sectionId === this.sectionId) {
      console.log('バリアント変更イベント受信:', event.data.variant?.id);
      this.debouncedUpdate();
    }
  }

  handleOptionChange(event) {
    // オプション選択変更時のイベントハンドラ
    console.log('オプション変更イベント受信');
    this.debouncedUpdate();
  }

  destroy() {
    // クリーンアップ
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    if (this.onVariantChangeUnsubscriber) {
      this.onVariantChangeUnsubscriber();
    }
    if (this.onOptionChangeUnsubscriber) {
      this.onOptionChangeUnsubscriber();
    }
  }
}

// 初期化関数
function initializeProductVariantPriceUpdater() {
  // 商品詳細ページの場合のみ実行
  const productInfo = document.querySelector('[id^="MainProduct-"]');
  if (productInfo) {
    // セクションIDを取得
    const sectionId = productInfo.id.replace('MainProduct-', '');
    
    console.log('商品詳細ページを検出、価格アップデーターを初期化:', sectionId);
    
    // 既存のインスタンスがあれば破棄
    if (window.productVariantPriceUpdater) {
      window.productVariantPriceUpdater.destroy();
    }
    
    // 価格アップデーターを初期化
    window.productVariantPriceUpdater = new ProductVariantPriceUpdater(sectionId);
  }
}

// DOMContentLoadedまたは既にDOMが読み込み済みの場合は即座に実行
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initializeProductVariantPriceUpdater, 100);
  });
} else {
  // DOMが既に読み込まれている場合
  setTimeout(initializeProductVariantPriceUpdater, 100);
}

// product-infoが後から読み込まれる場合に備えて、カスタムイベントでも初期化
document.addEventListener('product-info:loaded', () => {
  console.log('product-info:loaded イベント受信');
  setTimeout(initializeProductVariantPriceUpdater, 200);
});

// windowのloadイベントでも試行（最終的なフォールバック）
window.addEventListener('load', () => {
  setTimeout(() => {
    if (!window.productVariantPriceUpdater) {
      console.log('load イベントでの初期化を試行');
      initializeProductVariantPriceUpdater();
    }
  }, 500);
});

// モジュールエクスポート（必要に応じて）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductVariantPriceUpdater;
}